import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  BiCreditCard,
  BiDevices,
  BiQrScan,
  BiWifiOff,
} from "react-icons/bi";
import PricingCard from "./PricingCard";
import PlanFinder from "./PlanFinder";
import {
  BillingCycle,
  PLANS,
  PlanNeeds,
  recommendPlan,
} from "./plans";
import {
  PaidPlanKey,
  WebPlan,
  loadWebPlans,
  planKey,
  purchaseWebPlan,
  takePendingPlan,
  webBillingEnabled,
} from "../../services/webBilling";
import { useToast } from "../../components/Toast/ToastContext";
import { trackMetaEvent } from "../../services/metaPixel";

const everyPlanIncludes = [
  { icon: BiCreditCard, label: "Square card payments" },
  { icon: BiQrScan, label: "Cash, manual, and QR payments" },
  { icon: BiWifiOff, label: "Cash and QR sales saved with no signal" },
  { icon: BiDevices, label: "iPhone, Android, and web" },
];

// Desktop puts Pro in the middle; phones list plans from cheapest up.
const cardOrder = { free: "", lite: "lg:order-3", pro: "lg:order-2" };

const PricingSection: React.FC = () => {
  const [billingCycle, setBillingCycle] = useState<BillingCycle>("monthly");
  const [needs, setNeeds] = useState<PlanNeeds>({
    events: 20,
    items: 100,
    hasStaff: false,
    needsPools: false,
  });
  const recommended = recommendPlan(needs);

  const user = localStorage.getItem("user");
  const userId = user ? JSON.parse(user)._id : null;
  const userEmail = user ? JSON.parse(user).email : null;
  const isPaid = user ? JSON.parse(user).isPaid : null;
  const userTier = user ? JSON.parse(user).tier : null;
  const { notify } = useToast();
  const [webPlans, setWebPlans] = useState<Record<string, WebPlan>>({});
  const [checkout, setCheckout] = useState<{
    key: string;
    error: string | null;
  } | null>(null);

  const startCheckout = useCallback(
    async (tier: PaidPlanKey, cycle: BillingCycle, plans = webPlans) => {
      const key = planKey(tier, cycle);
      const webPlan = plans[key];
      if (!userId || !userEmail) return;
      if (!webPlan) {
        setCheckout({
          key,
          error:
            "This plan isn't available on the website yet. Please subscribe in the app.",
        });
        return;
      }
      setCheckout({ key, error: null });
      try {
        const result = await purchaseWebPlan(userId, userEmail, webPlan);
        if (result === "purchased") {
          const price =
            cycle === "monthly" ? PLANS[tier].monthlyPrice : PLANS[tier].annualPrice;
          const content_name = `VendorOrbis ${PLANS[tier].title} ${cycle}`;
          // Same IDs the server uses for these events, so Meta counts them once.
          const productId = webPlan.rcPackage.webBillingProduct.identifier;
          // A trial costs nothing today; report what it's expected to be worth.
          if (webPlan.trialDays) {
            trackMetaEvent(
              "StartTrial",
              { value: 0, currency: "USD", predicted_ltv: price, content_name },
              `trial-${userId}-${productId}`
            );
          } else {
            trackMetaEvent(
              "Purchase",
              { value: price, currency: "USD", content_name },
              `purchase-${userId}-${productId}`
            );
          }
          notify(`You're on ${PLANS[tier].title}. Thanks for subscribing!`, {
            type: "success",
          });
          window.location.href = "/billing";
          return;
        }
        setCheckout(null);
      } catch (error: any) {
        console.error("Web checkout failed", error);
        setCheckout({
          key,
          error:
            "Checkout couldn't open. Please try again, or subscribe in the app.",
        });
      }
    },
    [webPlans, userId, userEmail, notify]
  );

  // Load web prices for a signed-in visitor, then open checkout for a plan
  // they picked before signing up.
  const pendingHandled = useRef(false);
  useEffect(() => {
    if (!webBillingEnabled || !userId) return;
    let cancelled = false;
    loadWebPlans(userId)
      .then((plans) => {
        if (cancelled) return;
        setWebPlans(plans);
        const pending = pendingHandled.current ? null : takePendingPlan();
        pendingHandled.current = true;
        if (pending && !isPaid) {
          setBillingCycle(pending.cycle);
          startCheckout(pending.tier, pending.cycle, plans);
        }
      })
      .catch((error) => console.error("Couldn't load web plans", error));
    return () => {
      cancelled = true;
    };
    // Load once per signed-in user; startCheckout changes with webPlans.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  return (
    <section className="relative isolate overflow-hidden bg-[#070b17] px-4 pb-24 pt-24 text-white md:px-8 md:pt-32">
      {/* Background: dot grid fading out from the top, plus two soft glows */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10 bg-[radial-gradient(rgba(148,163,184,0.18)_1px,transparent_1px)] [background-size:24px_24px] [mask-image:radial-gradient(ellipse_70%_55%_at_50%_0%,black,transparent)]"
      />
      <div
        aria-hidden
        className="absolute -top-40 left-1/2 -z-10 h-[520px] w-[820px] -translate-x-1/2 rounded-full bg-blue-600/25 blur-[120px]"
      />
      <div
        aria-hidden
        className="absolute right-[-10%] top-[35%] -z-10 h-[420px] w-[420px] rounded-full bg-violet-600/15 blur-[120px]"
      />

      <div className="mx-auto max-w-3xl text-center">
        <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-medium text-slate-300">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
          Square integration included on every plan
        </span>
        <h1 className="mt-6 text-4xl font-bold tracking-tight md:text-6xl">
          Pricing that grows{" "}
          <span className="bg-gradient-to-r from-blue-400 via-indigo-300 to-violet-400 bg-clip-text text-transparent sm:block">
            with your booth
          </span>
        </h1>
        <p className="mx-auto mt-5 max-w-xl text-base text-slate-400 md:text-lg">
          VendorOrbis is free for up to 10 active events and 25 items, with no
          credit card and no time limit. When your booth grows, try Pro free
          for 30 days.
        </p>

        <div className="relative mt-10 inline-grid grid-cols-2 rounded-full border border-white/10 bg-white/5 p-1 text-sm font-medium">
          <span
            aria-hidden
            className={`absolute inset-y-1 left-1 w-[calc(50%-4px)] rounded-full bg-blue-600 shadow-lg shadow-blue-600/30 transition-transform duration-300 ${
              billingCycle === "annual" ? "translate-x-full" : ""
            }`}
          />
          {(["monthly", "annual"] as BillingCycle[]).map((cycle) => (
            <button
              key={cycle}
              type="button"
              aria-pressed={billingCycle === cycle}
              onClick={() => setBillingCycle(cycle)}
              className={`relative z-10 rounded-full px-7 py-2 transition-colors ${
                billingCycle === cycle
                  ? "text-white"
                  : "text-slate-400 hover:text-white"
              }`}
            >
              {cycle === "monthly" ? "Monthly" : "Yearly"}
            </button>
          ))}
        </div>
        {webBillingEnabled && (
          <p className="mt-3 text-sm font-medium text-emerald-300">
            Pay yearly on the website and get 2 months free
          </p>
        )}
      </div>

      <div className="mx-auto mt-14 grid max-w-6xl items-stretch gap-6 lg:grid-cols-3 lg:gap-5">
        {(["free", "lite", "pro"] as const).map((key) => (
          <div
            key={key}
            className={`mx-auto w-full max-w-md ${cardOrder[key]} ${
              key === "pro" ? "lg:-my-4" : "lg:my-4"
            }`}
          >
            <PricingCard
              plan={PLANS[key]}
              billingCycle={billingCycle}
              recommended={recommended === key}
              userId={userId}
              userEmail={userEmail}
              isPaid={isPaid}
              userTier={userTier}
              buttonLabel={
                key === "free" && userTier === "free"
                  ? "Current Plan"
                  : undefined
              }
              {...(key !== "free" && {
                webPlan: webPlans[planKey(key, billingCycle)],
                onCheckout: () => startCheckout(key, billingCycle),
                checkoutBusy: checkout?.key === planKey(key, billingCycle) && !checkout.error,
                checkoutError:
                  checkout?.key === planKey(key, billingCycle) ? checkout.error : null,
              })}
            />
          </div>
        ))}
      </div>

      <div className="mx-auto mt-14 max-w-5xl">
        <p className="text-center text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
          Every plan includes
        </p>
        <ul className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-4">
          {everyPlanIncludes.map(({ icon: Icon, label }) => (
            <li
              key={label}
              className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-slate-300"
            >
              <Icon size={20} className="flex-shrink-0 text-blue-400" />
              {label}
            </li>
          ))}
        </ul>
      </div>

      <PlanFinder
        needs={needs}
        onChange={setNeeds}
        recommended={recommended}
        billingCycle={billingCycle}
      />
    </section>
  );
};

export default PricingSection;
